export class PlatformClientError extends Error {
    code;
    requestId;
    retryable;
    details;
    status;
    constructor(error, status) {
        super(error.message);
        this.name = "PlatformClientError";
        this.code = error.code;
        this.requestId = error.requestId;
        this.retryable = error.retryable;
        if (error.details !== undefined)
            this.details = error.details;
        this.status = status;
    }
}
class Transport {
    options;
    baseUrl;
    fetcher;
    constructor(options) {
        this.options = options;
        this.baseUrl = (options.baseUrl ?? "/api/v1").replace(/\/$/, "");
        this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
    }
    async token() {
        return typeof this.options.token === "function"
            ? await this.options.token()
            : this.options.token;
    }
    async raw(path, init = {}, admin = false) {
        const token = await this.token();
        const headers = new Headers(this.options.defaultHeaders);
        if (token)
            headers.set("Authorization", `Bearer ${token}`);
        if (!(init.body instanceof FormData) && init.body !== undefined) {
            headers.set("Content-Type", "application/json");
        }
        new Headers(init.headers).forEach((value, key) => headers.set(key, value));
        const url = `${admin ? this.baseUrl.replace(/\/v1$/, "/admin/v1") : this.baseUrl}${path}`;
        return this.fetcher(url, {
            ...init,
            credentials: "same-origin",
            headers,
        });
    }
    async request(path, init = {}) {
        const response = await this.raw(path, init);
        if (!response.ok) {
            const fallback = {
                code: "execution_failed",
                message: `PwC AI Workspace request failed (${response.status})`,
                requestId: response.headers.get("X-Request-Id") ?? "",
                retryable: response.status >= 500,
            };
            let error = fallback;
            try {
                error = { ...fallback, ...(await response.json()) };
            }
            catch {
                // Normalize non-JSON proxy errors without exposing their body.
            }
            throw new PlatformClientError(error, response.status);
        }
        if (response.status === 204)
            return undefined;
        return (await response.json());
    }
}
function jsonBody(value) {
    return JSON.stringify(value);
}
function mutationHeaders(options) {
    const headers = {};
    if (options?.idempotencyKey) {
        headers["Idempotency-Key"] = options.idempotencyKey;
    }
    if (options?.ifMatch)
        headers["If-Match"] = options.ifMatch;
    return headers;
}
function requestSignal(signal) {
    return signal ? { signal } : {};
}
async function* sse(response, signal) {
    if (!response.ok) {
        let error = {
            code: "execution_failed",
            message: `Stream request failed (${response.status})`,
            requestId: response.headers.get("X-Request-Id") ?? "",
            retryable: response.status >= 500,
        };
        try {
            error = { ...error, ...(await response.json()) };
        }
        catch {
            // Keep normalized error.
        }
        throw new PlatformClientError(error, response.status);
    }
    if (!response.body)
        throw new Error("Streaming response has no body");
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    try {
        while (true) {
            if (signal?.aborted)
                throw signal.reason;
            const { value, done } = await reader.read();
            if (done)
                break;
            buffer += decoder.decode(value, { stream: true });
            const frames = buffer.split("\n\n");
            buffer = frames.pop() ?? "";
            for (const frame of frames) {
                const payload = frame
                    .split("\n")
                    .filter((line) => line.startsWith("data:"))
                    .map((line) => line.slice(5).trim())
                    .join("\n");
                if (payload && payload !== "[DONE]") {
                    yield JSON.parse(payload);
                }
            }
        }
    }
    finally {
        reader.releaseLock();
    }
}
function createApi(transport) {
    return {
        ai: {
            generate(request, options) {
                return transport.request("/ai/generate", {
                    method: "POST",
                    body: jsonBody(request),
                    headers: mutationHeaders(options),
                    ...requestSignal(options?.signal),
                });
            },
            async *stream(request, options) {
                const response = await transport.raw("/ai/stream", {
                    method: "POST",
                    body: jsonBody(request),
                    headers: mutationHeaders(options),
                    ...requestSignal(options?.signal),
                });
                yield* sse(response, options?.signal);
            },
            embeddings(request, options) {
                return transport.request("/ai/embeddings", {
                    method: "POST",
                    body: jsonBody(request),
                    headers: mutationHeaders(options),
                    ...requestSignal(options?.signal),
                });
            },
            models() {
                return transport.request("/ai/models");
            },
        },
        operations: {
            invoke(operationId, request, options) {
                const query = options?.version
                    ? `?version=${encodeURIComponent(options.version)}`
                    : "";
                return transport.request(`/operations/${encodeURIComponent(operationId)}:invoke${query}`, {
                    method: "POST",
                    body: jsonBody(request),
                    headers: mutationHeaders({
                        idempotencyKey: options?.idempotencyKey,
                    }),
                    ...requestSignal(options?.signal),
                });
            },
            start(operationId, request, options) {
                const query = options?.version
                    ? `?version=${encodeURIComponent(options.version)}`
                    : "";
                return transport.request(`/operations/${encodeURIComponent(operationId)}:start${query}`, {
                    method: "POST",
                    body: jsonBody(request),
                    headers: mutationHeaders({
                        idempotencyKey: options?.idempotencyKey,
                    }),
                });
            },
        },
        capabilities: {
            invoke(capabilityId, request, options) {
                const query = options?.version
                    ? `?version=${encodeURIComponent(options.version)}`
                    : "";
                return transport.request(`/capabilities/${encodeURIComponent(capabilityId)}:invoke${query}`, {
                    method: "POST",
                    body: jsonBody(request),
                    headers: mutationHeaders({
                        idempotencyKey: options?.idempotencyKey,
                    }),
                    ...requestSignal(options?.signal),
                });
            },
        },
        runtime: {
            start(request, options) {
                return transport.request("/runtime/runs", {
                    method: "POST",
                    body: jsonBody(request),
                    headers: mutationHeaders(options),
                    ...requestSignal(options?.signal),
                });
            },
            get(runId) {
                return transport.request(`/runtime/runs/${encodeURIComponent(runId)}`);
            },
            async *subscribe(runId, options) {
                let sequence = options?.afterSequence ?? 0;
                while (!options?.signal?.aborted) {
                    const events = await transport.request(`/runtime/runs/${encodeURIComponent(runId)}/events?afterSequence=${sequence}`, requestSignal(options?.signal));
                    for (const event of events) {
                        sequence = Math.max(sequence, event.sequence);
                        yield event;
                    }
                    if (events.some((event) => [
                        "run.completed",
                        "run.failed",
                        "run.cancelled",
                        "run.interrupted",
                    ].includes(event.type))) {
                        return;
                    }
                    await new Promise((resolve) => setTimeout(resolve, 750));
                }
            },
            resume(runId, request, options) {
                return transport.request(`/runtime/runs/${encodeURIComponent(runId)}/resume`, {
                    method: "POST",
                    body: jsonBody(request),
                    headers: mutationHeaders(options),
                    ...requestSignal(options?.signal),
                });
            },
            cancel(runId, options) {
                return transport.request(`/runtime/runs/${encodeURIComponent(runId)}/cancel`, {
                    method: "POST",
                    body: "{}",
                    headers: mutationHeaders(options),
                    ...requestSignal(options?.signal),
                });
            },
        },
    };
}
function createData(transport) {
    return {
        list(resource, query) {
            const suffix = query
                ? `?query=${encodeURIComponent(JSON.stringify(query))}`
                : "";
            return transport.request(`/data/${encodeURIComponent(resource)}${suffix}`);
        },
        get(resource, id, options) {
            return transport.request(`/data/${encodeURIComponent(resource)}/${encodeURIComponent(id)}`, requestSignal(options?.signal));
        },
        create(resource, value, options) {
            return transport.request(`/data/${encodeURIComponent(resource)}`, {
                method: "POST",
                body: jsonBody(options?.spaceId &&
                    typeof value === "object" &&
                    value !== null
                    ? { ...value, spaceId: options.spaceId }
                    : value),
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
        update(resource, id, patch, options) {
            return transport.request(`/data/${encodeURIComponent(resource)}/${encodeURIComponent(id)}`, {
                method: "PATCH",
                body: jsonBody(patch),
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
        delete(resource, id, options) {
            return transport.request(`/data/${encodeURIComponent(resource)}/${encodeURIComponent(id)}`, {
                method: "DELETE",
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
    };
}
function stateQuery(value) {
    return value.scope
        ? `?scope=${encodeURIComponent(value.scope)}`
        : "";
}
function createState(transport) {
    return {
        get(request) {
            return transport.request(`/storage/state/${encodeURIComponent(request.namespace)}/${encodeURIComponent(request.key)}${stateQuery(request)}`);
        },
        put(request) {
            return transport.request(`/storage/state/${encodeURIComponent(request.namespace)}/${encodeURIComponent(request.key)}`, {
                method: "PUT",
                body: jsonBody({
                    value: request.value,
                    scope: request.scope,
                    ifMatch: request.ifMatch,
                    idempotencyKey: request.idempotencyKey,
                    ttlSeconds: request.ttlSeconds,
                }),
                headers: mutationHeaders({
                    ifMatch: request.ifMatch,
                    idempotencyKey: request.idempotencyKey,
                }),
            });
        },
        delete(request) {
            return transport.request(`/storage/state/${encodeURIComponent(request.namespace)}/${encodeURIComponent(request.key)}${stateQuery(request)}`, { method: "DELETE" });
        },
        list(request) {
            const params = new URLSearchParams();
            if (request.scope)
                params.set("scope", request.scope);
            if (request.prefix)
                params.set("prefix", request.prefix);
            if (request.limit)
                params.set("limit", String(request.limit));
            if (request.cursor)
                params.set("cursor", request.cursor);
            return transport.request(`/storage/state/${encodeURIComponent(request.namespace)}?${params}`);
        },
    };
}
function createObjects(transport) {
    return {
        list(resource, query) {
            const params = new URLSearchParams();
            if (query?.limit)
                params.set("limit", String(query.limit));
            if (query?.cursor)
                params.set("cursor", query.cursor);
            if (query?.prefix)
                params.set("prefix", query.prefix);
            if (query?.spaceId)
                params.set("spaceId", query.spaceId);
            return transport.request(`/storage/objects/${encodeURIComponent(resource)}?${params}`);
        },
        get(resource, objectId, version) {
            const suffix = version
                ? `?version=${encodeURIComponent(version)}`
                : "";
            return transport.request(`/storage/objects/${encodeURIComponent(resource)}/${encodeURIComponent(objectId)}${suffix}`);
        },
        put(resource, input, options) {
            if (!input.content) {
                return Promise.reject(new Error("ObjectUpload.content Blob is required by the browser SDK"));
            }
            const form = new FormData();
            form.set("file", input.content, input.name);
            form.set("metadata", JSON.stringify(input.metadata ?? {}));
            if (input.objectId)
                form.set("objectId", input.objectId);
            if (input.spaceId)
                form.set("spaceId", input.spaceId);
            return transport.request(`/storage/objects/${encodeURIComponent(resource)}`, {
                method: "POST",
                body: form,
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
        delete(resource, objectId, options) {
            return transport.request(`/storage/objects/${encodeURIComponent(resource)}/${encodeURIComponent(objectId)}`, {
                method: "DELETE",
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
        download(resource, objectId, version) {
            const suffix = version
                ? `?version=${encodeURIComponent(version)}`
                : "";
            return transport.request(`/storage/objects/${encodeURIComponent(resource)}/${encodeURIComponent(objectId)}/download${suffix}`);
        },
    };
}
function createKnowledge(transport) {
    return {
        ingest(request, options) {
            return transport.request("/knowledge/ingest", {
                method: "POST",
                body: jsonBody(request),
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
        search(request) {
            return transport.request("/knowledge/search", {
                method: "POST",
                body: jsonBody(request),
            });
        },
        citation(documentId, chunkId) {
            return transport.request(`/knowledge/documents/${encodeURIComponent(documentId)}/chunks/${encodeURIComponent(chunkId)}`);
        },
        delete(documentId, options) {
            return transport.request(`/knowledge/documents/${encodeURIComponent(documentId)}`, {
                method: "DELETE",
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
    };
}
function createFiles(transport) {
    return {
        upload(file, options) {
            const form = new FormData();
            form.set("file", file);
            return transport.request("/files", {
                method: "POST",
                body: form,
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
        get(fileId) {
            return transport.request(`/files/${encodeURIComponent(fileId)}`);
        },
        download(fileId) {
            return transport.request(`/files/${encodeURIComponent(fileId)}/download`);
        },
        convert(fileId, targetMediaType, options) {
            return transport.request(`/files/${encodeURIComponent(fileId)}/convert`, {
                method: "POST",
                body: jsonBody({ targetMediaType }),
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
        artifacts() {
            return transport.request("/artifacts");
        },
    };
}
function createJobs(transport) {
    const client = {
        get(jobId) {
            return transport.request(`/jobs/${encodeURIComponent(jobId)}`);
        },
        async wait(jobId, options) {
            const interval = options?.intervalMs ?? 750;
            while (true) {
                if (options?.signal?.aborted)
                    throw options.signal.reason;
                const job = await client.get(jobId);
                if (["succeeded", "failed", "cancelled"].includes(job.status)) {
                    return job;
                }
                await new Promise((resolve) => setTimeout(resolve, interval));
            }
        },
        cancel(jobId, options) {
            return transport.request(`/jobs/${encodeURIComponent(jobId)}`, {
                method: "DELETE",
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
        async *subscribe(jobId, options) {
            let sequence = options?.afterSequence ?? 0;
            while (!options?.signal?.aborted) {
                const events = await transport.request(`/jobs/${encodeURIComponent(jobId)}/events?afterSequence=${sequence}`, requestSignal(options?.signal));
                for (const event of events) {
                    sequence = Math.max(sequence, event.sequence);
                    yield event;
                }
                if (events.some((event) => ["job.succeeded", "job.failed", "job.cancelled"].includes(event.type))) {
                    return;
                }
                await new Promise((resolve) => setTimeout(resolve, 750));
            }
        },
    };
    return client;
}
function createAutomation(transport) {
    return {
        listTemplates() {
            return transport.request("/automation/templates");
        },
        listInstances(query) {
            const params = new URLSearchParams();
            if (query?.enabled !== undefined) {
                params.set("enabled", String(query.enabled));
            }
            if (query?.templateId) {
                params.set("templateId", query.templateId);
            }
            return transport.request(`/automation/instances?${params}`);
        },
        createInstance(templateId, input, options) {
            return transport.request("/automation/instances", {
                method: "POST",
                body: jsonBody({ templateId, ...input }),
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
        pause(triggerId, options) {
            return transport.request(`/automation/instances/${encodeURIComponent(triggerId)}/pause`, {
                method: "POST",
                body: "{}",
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
        resume(triggerId, options) {
            return transport.request(`/automation/instances/${encodeURIComponent(triggerId)}/resume`, {
                method: "POST",
                body: "{}",
                headers: mutationHeaders(options),
                ...requestSignal(options?.signal),
            });
        },
        listRuns(triggerId) {
            return transport.request(`/automation/instances/${encodeURIComponent(triggerId)}/runs`);
        },
    };
}
export function createAgentContext(options) {
    const transport = new Transport(options);
    const state = createState(transport);
    const objects = createObjects(transport);
    const storage = { state, objects };
    return {
        agentId: options.agentId,
        releaseId: options.releaseId,
        basePath: options.basePath,
        initialPath: options.initialPath,
        session: options.session,
        theme: options.theme,
        locale: options.locale ?? "ko-KR",
        config: options.config ?? {},
        api: createApi(transport),
        data: createData(transport),
        storage,
        knowledge: createKnowledge(transport),
        files: createFiles(transport),
        jobs: createJobs(transport),
        automation: createAutomation(transport),
        bus: options.bus,
    };
}
//# sourceMappingURL=client.js.map